import { slotsForPlane, type SlotId } from "../../utils/hangingProtocolService";
import type { PlaneOrientation } from "../../viewport/types";

/** Where a requested reformat goes, or why it was refused. */
export type ReformatTarget =
  | { slotId: SlotId }
  | { refused: "segmentation-layout" | "no-slot-for-plane" };

/**
 * Resolve a reformat request to a destination pane.
 *
 * Pure so the refusal rules are testable without mounting the grid. The
 * segmentation refusal is the load-bearing one: `resolveGridReslice` will not
 * reslice in a segmentation layout, so a slot carrying `reslice` there
 * would hold the AXIAL series under a Coronal label with no reslice and no
 * badge — a silent wrong-plane display. Refusing is not an optimization.
 *
 * When a plane has more than one configured slot, prefer the SECOND one
 * (layout order) over the first. General rule, not an Axial special case: a
 * generated (reformatted) pane must never displace the PRIMARY acquired pane
 * for that plane — in the classification layout that's Axial's top-left,
 * usually the acquired axial the reader is working from, and Axial is the
 * only plane with two slots today, so this only bites Axial in practice.
 * Coronal and sagittal each have exactly one configured slot, so the rule is
 * a no-op for them. Falls back to the first (only) slot when the layout
 * doesn't have a second one.
 *
 * Known limitation: if the second slot holds a native series while the first
 * is empty, this still evicts the native one instead of landing in the empty
 * first slot. That eviction is routed through `handleBeforeSeriesDrop`, but
 * that guard only emits an acquisition-mismatch toast (with an Undo) when the
 * reformat's source series is from a DIFFERENT acquisition group than the
 * grid's dominant one (`checkAcquisitionGroupMismatch` returns non-null).
 * When the source shares the dominant group — the normal case, reformatting
 * the axial you're already reading — `handleBeforeSeriesDrop` returns `true`
 * before emitting anything, so there is NO toast and NO Undo: the native
 * series in the second slot is replaced silently. That matches how a plain
 * drag-drop onto that pane behaves, so it isn't a gap this file introduces —
 * but it IS a known, accepted gap, not a safe one.
 */
export function resolveReformatTarget(
  plane: PlaneOrientation,
  isSegmentationProject: boolean,
): ReformatTarget {
  if (isSegmentationProject) return { refused: "segmentation-layout" };
  const slots = slotsForPlane(plane);
  if (slots.length === 0) return { refused: "no-slot-for-plane" };
  return { slotId: slots[1] ?? slots[0] };
}
